import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { ArrowLeft, MapPin, Trophy, Medal, Crown, Share2, Download, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { id: 'weekly', label: 'This week' },
  { id: 'monthly', label: 'This month' },
  { id: 'all', label: 'All-time' },
];

const rankBadgeColor = (rank) => {
  if (rank === 1) return 'from-amber-400 to-yellow-500';
  if (rank === 2) return 'from-slate-300 to-slate-400';
  if (rank === 3) return 'from-orange-400 to-orange-500';
  return 'from-slate-100 to-slate-200';
};

const StateLeaderboard = () => {
  const { state: stateParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [period, setPeriod] = useState('weekly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statesList, setStatesList] = useState([]);
  const [exporting, setExporting] = useState(false);
  const canvasRef = useRef(null);

  const stateName = decodeURIComponent(stateParam || '');

  const fetchData = useCallback(async () => {
    if (!stateName) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/leaderboard/state/${encodeURIComponent(stateName)}`, { params: { period } });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load leaderboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [stateName, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    axios.get(`${API_URL}/api/leaderboard/states`).then(r => setStatesList(r.data?.states || [])).catch(() => {});
  }, []);

  // Build share card via canvas
  const buildShareCard = useCallback(() => {
    if (!data || !canvasRef.current) return null;
    const canvas = canvasRef.current;
    const W = 1080, H = 1350;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    // BG
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#0F172A');
    bg.addColorStop(1, '#1E293B');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Top stripe
    const strip = ctx.createLinearGradient(0, 0, W, 0);
    strip.addColorStop(0, '#0EA5E9');
    strip.addColorStop(1, '#FBBF24');
    ctx.fillStyle = strip;
    ctx.fillRect(0, 0, W, 16);

    // Brand
    ctx.fillStyle = '#7DD3FC';
    ctx.font = 'bold 38px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('APTIVON', 64, 100);
    ctx.fillStyle = '#94A3B8';
    ctx.font = '24px Inter, sans-serif';
    ctx.fillText('JEE / NEET Quiz Battles', 64, 138);

    // State pin
    ctx.fillStyle = '#FBBF24';
    ctx.font = 'bold 32px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('📍', W - 220, 110);
    ctx.fillStyle = '#F8FAFC';
    ctx.font = 'bold 34px Inter, sans-serif';
    ctx.fillText(data.state, W - 64, 110);
    ctx.fillStyle = '#94A3B8';
    ctx.font = '22px Inter, sans-serif';
    ctx.fillText(PERIODS.find(p => p.id === data.period)?.label || data.period, W - 64, 145);

    // Title
    ctx.fillStyle = '#F8FAFC';
    ctx.font = 'bold 64px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`Top scorers in ${data.state}`, W / 2, 260);
    ctx.fillStyle = '#94A3B8';
    ctx.font = '28px Inter, sans-serif';
    ctx.fillText(`${data.total_players} players competing`, W / 2, 305);

    // Rows
    const rows = (data.top || []).slice(0, 10);
    const rowH = 88;
    const startY = 360;
    rows.forEach((r, i) => {
      const y = startY + i * rowH;
      // Row bg
      ctx.fillStyle = i % 2 === 0 ? '#1E293B' : '#0F172A';
      ctx.fillRect(64, y, W - 128, rowH - 8);

      // Rank pill
      const rankColors = ['#FBBF24', '#CBD5E1', '#FB923C'];
      ctx.fillStyle = rankColors[i] || '#334155';
      ctx.beginPath();
      ctx.arc(120, y + (rowH - 8) / 2, 28, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#0F172A';
      ctx.font = 'bold 28px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(r.rank), 120, y + (rowH - 8) / 2 + 10);

      // Name
      ctx.fillStyle = '#F8FAFC';
      ctx.font = 'bold 30px Inter, sans-serif';
      ctx.textAlign = 'left';
      const name = (r.user_name || 'Anonymous').slice(0, 26);
      ctx.fillText(name, 180, y + 40);
      ctx.fillStyle = '#94A3B8';
      ctx.font = '22px Inter, sans-serif';
      ctx.fillText(`${r.quizzes_played} quizzes · ${r.accuracy}% accuracy`, 180, y + 70);

      // Score
      ctx.fillStyle = '#FBBF24';
      ctx.font = 'bold 34px Inter, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`${r.total_score} pts`, W - 100, y + 55);
    });

    // CTA at bottom
    const ctaY = startY + rows.length * rowH + 30;
    ctx.fillStyle = '#0EA5E9';
    ctx.fillRect(64, ctaY, W - 128, 140);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 38px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Can you make this list?', W / 2, ctaY + 60);
    ctx.font = '28px Inter, sans-serif';
    ctx.fillText('Play free at aptivon.in', W / 2, ctaY + 105);

    return canvas.toDataURL('image/png');
  }, [data]);

  const dataUrlToBlob = (dataUrl) => {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/data:(.*?);/)[1];
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  };

  const shareCaption = data ? (
    `🏆 ${data.state} leaderboard on Aptivon — ${data.total_players} aspirants battling for the top spot.\n\nTop 3 ${PERIODS.find(p => p.id === data.period)?.label.toLowerCase()}:\n${(data.top || []).slice(0, 3).map(r => `${r.rank}. ${r.user_name} — ${r.total_score} pts`).join('\n')}\n\nThink you can break the top 10? Play free → https://aptivon.in${user?.referral_code ? `/register?ref=${user.referral_code}` : ''}`
  ) : '';

  const handleDownload = () => {
    const url = buildShareCard();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `aptivon-${data.state.replace(/\s+/g, '-').toLowerCase()}-leaderboard.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Saved! Post it 🔥');
  };

  const handleShare = async () => {
    setExporting(true);
    try {
      const url = buildShareCard();
      if (!url) return;
      const blob = dataUrlToBlob(url);
      const file = new File([blob], 'leaderboard.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shareCaption, title: `${data.state} leaderboard` });
      } else {
        handleDownload();
        await navigator.clipboard.writeText(shareCaption);
        toast.success('Image saved & caption copied!');
      }
    } catch (e) { /* user cancelled */ } finally {
      setExporting(false);
    }
  };

  const handleWhatsApp = () => {
    handleDownload();
    setTimeout(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareCaption)}`, '_blank', 'noopener,noreferrer');
    }, 250);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="state-leaderboard-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#6B7280] hover:text-[#0EA5E9] mb-5 transition"
          data-testid="state-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-5 h-5 text-[#0EA5E9]" />
              <h1 className="text-3xl sm:text-4xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {stateName} Leaderboard
              </h1>
            </div>
            <p className="text-sm text-[#6B7280]">
              {data?.total_players ?? '—'} aspirants in your state. Climb fast.
            </p>
          </div>
          <div className="inline-flex bg-white border border-slate-200 rounded-full p-1" data-testid="period-toggle">
            {PERIODS.map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${period === p.id ? 'bg-[#0EA5E9] text-white' : 'text-[#6B7280] hover:text-[#0EA5E9]'}`}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Share strip */}
        {data && data.top?.length > 0 && (
          <div className="rounded-2xl border border-[#0EA5E9]/20 bg-gradient-to-r from-[#0EA5E9]/5 to-[#FBBF24]/5 p-4 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="share-strip">
            <div className="flex items-center gap-2 text-sm text-[#374151]">
              <Trophy className="w-4 h-4 text-[#FBBF24]" />
              <span>Brag about your state — share this list with friends.</span>
            </div>
            <div className="flex gap-2">
              <button onClick={handleWhatsApp} className="px-3 py-2 rounded-xl bg-[#25D366] text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="share-state-whatsapp">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </button>
              <button onClick={handleShare} disabled={exporting} className="px-3 py-2 rounded-xl bg-amber-400 text-[#111827] text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="share-state-share">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} Share
              </button>
              <button onClick={handleDownload} className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-[#0EA5E9] text-xs font-bold inline-flex items-center gap-1.5" data-testid="share-state-download">
                <Download className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="py-20 text-center text-[#9CA3AF]">
            <Loader2 className="w-8 h-8 animate-spin inline" />
          </div>
        ) : !data?.top?.length ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="empty-state">
            <Crown className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <h3 className="font-bold text-[#111827] mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              No scorers yet
            </h3>
            <p className="text-sm text-[#6B7280] mb-4">
              Be the first to top the {data?.state || stateName} board {period === 'weekly' ? 'this week' : period === 'monthly' ? 'this month' : ''}.
            </p>
            <Link to="/quiz-pools" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-[#0EA5E9] text-white font-bold text-sm">
              Play a quiz <Trophy className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <ol className="space-y-2" data-testid="state-leaderboard-list">
            {data.top.map((r) => (
              <li
                key={r.rank}
                className={`flex items-center gap-4 p-4 rounded-2xl bg-white border ${r.rank <= 3 ? 'border-amber-200 shadow-sm' : 'border-slate-200'}`}
                data-testid={`state-row-${r.rank}`}
              >
                <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${rankBadgeColor(r.rank)} flex items-center justify-center shrink-0 shadow-sm`}>
                  {r.rank <= 3 ? <Medal className="w-5 h-5 text-white" /> : <span className="font-black text-[#111827]">{r.rank}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#111827] truncate">{r.user_name}</p>
                  <p className="text-xs text-[#6B7280]">{r.quizzes_played} quizzes · {r.accuracy}% accuracy</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-black text-[#0EA5E9]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {r.total_score}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">points</p>
                </div>
              </li>
            ))}
          </ol>
        )}

        {/* Switch state */}
        {statesList.length > 0 && (
          <div className="mt-10">
            <h3 className="text-sm font-bold text-[#111827] mb-3">Switch state</h3>
            <div className="flex flex-wrap gap-2">
              {statesList.filter(s => s.players > 0 && s.state !== stateName).slice(0, 12).map((s) => (
                <Link
                  key={s.state}
                  to={`/leaderboard/state/${encodeURIComponent(s.state)}`}
                  className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-bold text-[#374151] hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition"
                  data-testid={`state-link-${s.state}`}
                >
                  {s.state} <span className="text-[#9CA3AF]">· {s.players}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </main>
    </div>
  );
};

export default StateLeaderboard;
